/* eslint react-hooks/exhaustive-deps: "warn" */

import { useContext } from 'react';

import { ContentContext } from 'context/ContentContext';

import { Button } from 'ui/atoms/Button/Button';
import { Faq } from 'ui/atoms/Faq/Faq';
import { Tabs } from 'ui/atoms/Tabs/Tabs';
import { ApiCodeBlock } from 'ui/molecules/ApiCodeBlock/ApiCodeBlock';
import { ApiEndpointExplorer } from 'ui/molecules/ApiEndpointExplorer/ApiEndpointExplorer';
import { ApiStatusRibbon } from 'ui/molecules/ApiStatusRibbon/ApiStatusRibbon';
import { ApiTerminal } from 'ui/molecules/ApiTerminal/ApiTerminal';
import { OurCustomersV2 } from 'ui/molecules/OurCustomers/OurCustomersV2';
import { Section } from 'ui/molecules/Section/Section';
import { AccessRoutes } from 'ui/organisms/AccessRoutes/AccessRoutes';
import { CtaSection } from 'ui/organisms/CtaSection/CtaSection';

import styles from './ApiPage.module.scss';

import {
  ENDPOINTS,
  FAQ_ITEMS,
  HERO_IDENTS,
  HERO_STEPS,
  LANG_SNIPPETS,
  STATUS_ITEMS,
  STATUS_NOTE,
  WHY_API_ROUTES,
} from './ApiPage.data';

const DOCS_URL = 'https://api.globaldatabase.com/docs/v2/';

const LANG_BADGES: Record<string, { symbol: string; color: string }> = {
  curl: { symbol: '$_', color: '#4b5c77' },
  py: { symbol: 'PY', color: '#2f74c0' },
  js: { symbol: 'JS', color: '#c98a1b' },
};

const ApiPage = () => {
  const { secondaryNavigation } = useContext(ContentContext);

  const freeTrialSlug = secondaryNavigation?.free_trial?.slug;

  const langTabs = LANG_SNIPPETS.map((snippet) => ({
    key: snippet.key,
    label: snippet.label,
    icon: (
      <span
        className={styles.langBadge}
        style={{
          color: LANG_BADGES[snippet.key]?.color,
          background: `${LANG_BADGES[snippet.key]?.color}1a`,
        }}
      >
        {LANG_BADGES[snippet.key]?.symbol}
      </span>
    ),
    content: (
      <ApiCodeBlock filename={snippet.filename} codeTokens={snippet.codeTokens} className={styles.codePanel} />
    ),
  }));

  return (
    <div className={styles.wrapper}>
      <Section
        layout="split"
        titleSize="page"
        extra="Company Data API"
        title={<>One API for the world{"'"}s company registries.</>}
        description={
          <>
            Verify companies, pull filed financials, map shareholders and group structures —{' '}
            <strong>registry-sourced JSON</strong> from 400+ official government sources, in one standardised schema.
          </>
        }
        background="linear-gradient(180deg, #f3f8ff 0%, #e4efff 60%, #ffffff 100%)"
        className={styles.hero}
        actions={
          <div className={styles.heroActions}>
            <div className={styles.heroCtas}>
              {freeTrialSlug && (
                <Button type="blue" size="xl" route={freeTrialSlug}>
                  Get your API key
                </Button>
              )}

              <a className={styles.docLink} href={DOCS_URL} target="_blank" rel="noreferrer">
                Read the docs →
              </a>
            </div>

            <div className={styles.idents}>
              <span>Look up by</span>
              {HERO_IDENTS.map((ident) => (
                <span key={ident} className={styles.ident}>
                  {ident}
                </span>
              ))}
            </div>
          </div>
        }
      >
        <ApiTerminal className={styles.heroTerminal} steps={HERO_STEPS} />
      </Section>

      <OurCustomersV2 hasBackground={false} />

      <Section
        align="left"
        extra="Endpoints"
        title="One ID. Every dataset."
        description="Search once to resolve the company, then reuse its ID across modular endpoints — combine them to match your onboarding, risk or enrichment workflow."
      >
        <ApiEndpointExplorer endpoints={ENDPOINTS} />
      </Section>

      <Section
        layout="split"
        extra="Integration"
        title="Your first call in under a minute."
        description="Token authentication, JSON in and out, predictable errors. Works from any language — and from any AI agent via our MCP server."
      >
        <div className={styles.codeShowcase}>
          <Tabs tabs={langTabs} defaultActiveKey="curl" size="lg" variant="underline" className={styles.langTabs} />
        </div>
      </Section>

      <ApiStatusRibbon items={STATUS_ITEMS} note={STATUS_NOTE} />

      <AccessRoutes
        background="light"
        numbered
        kicker="Why this API"
        heading="Registry data, built for production."
        lede="The same primary-source data that powers our platform — verifiable, standardised and licensed for what you're actually building."
        routes={WHY_API_ROUTES}
      />

      <CtaSection
        theme="dark"
        glow
        title="Make your first call today."
        description="Get an API key, resolve your first company, and see registry-sourced JSON come back — before your trial coffee goes cold."
        actions={
          <>
            {freeTrialSlug && (
              <Button type="white" size="xl" route={freeTrialSlug}>
                Get your API key
              </Button>
            )}

            <Button type="white-outline" size="xl" href={DOCS_URL}>
              Read the docs
            </Button>
          </>
        }
      />

      <Faq data={FAQ_ITEMS} title="The Company Data API, answered." />
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default ApiPage;
